import * as yup from "yup";
import disposableDomains from "disposable-email-domains";
import mailcheck from "mailcheck";

// Common field validators
export const requiredString = (label) =>
  yup.string().required(`${label} is required`);

export const emailValidator = yup
  .string()
  .email("Enter a valid email")
  .required("Email is required");

export const noDisposableEmailValidator = yup
  .string()
  .email("Enter a valid email")
  .test("no-typos", "Invalid email please enter a valid email", (value) => {
    if (!value || !value.includes("@")) return true;

    const suggestion = mailcheck.run({ email: value });
    if (suggestion) {
      return false;
    }

    return true;
  })
  .test("is-not-disposable", "Email address are not allowed", async (value) => {
    if (!value || !value.includes("@")) return true;
    const parts = value.split("@");
    if (parts.length < 2 || !parts[1]) return true;

    const domain = parts[1];

    if (disposableDomains.includes(domain)) return false;

    try {
      const res = await fetch(`https://disposable.debounce.io/?email=${value}`);
      const data = await res.json();
      if (data.disposable === "true") return false;
    } catch (err) {
      // Ignore network errors to avoid blocking legit users
    }
    return true;
  })
  .required("Email is required");

export const passwordValidator = yup
  .string()
  .min(6, "Password must be at least 6 characters")
  .matches(/[0-9]/, "Password must contain at least one number")
  .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
  .required("Password is required");

export const loginpasswordValidator = yup
  .string()
  .required("Password is required");

const isValidWebsiteUrl = (value) => {
  if (!value?.trim()) return false;
  const trimmed = value.trim();
  try {
    const urlToTest = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const { hostname } = new URL(urlToTest);
    return hostname === "localhost" || hostname.includes(".");
  } catch {
    return false;
  }
};

export const websiteValidator = (message = "Invalid URL") =>
  yup
    .string()
    .required("Website is required")
    .test("is-valid-website", message, isValidWebsiteUrl);

// Example: Full form schema
export const signUpSchema = yup.object().shape({
  firstName: requiredString("First Name"),
  lastName: requiredString("Last Name"),
  email: noDisposableEmailValidator,
  mobileNumber: yup
    .string()
    .matches(/^[0-9]{7,15}$/, "Invalid phone number")
    .required("Phone number is required"),
  password: passwordValidator,
  confirmPwd: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords not match")
    .required("Confirm password is required"),
  otp: yup.string().required("OTP is required"),
  privacyPolicy: yup.boolean().oneOf([true], "You must agree to the Privacy Policy"),
  termsOfService: yup.boolean().oneOf([true], "You must agree to the Terms of Service"),
  editorialGuidelines: yup.boolean().oneOf([true], "You must agree to the Editorial Guidelines"),
});


export const signInSchema = yup.object().shape({
  username: emailValidator,
  password: loginpasswordValidator,
  username: emailValidator,
  password: loginpasswordValidator
});

export const forgotSchema = yup.object().shape({
  email: emailValidator,
  otp: yup
    .string()
    .required("OTP is required"),
  password: passwordValidator,
  confirmPwd: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords not match")
    .required("Confirm password is required"),
});

export const newpasswordSchema = yup.object().shape({
  // email : emailValidator,
  // otp: yup
  // .string()
  // .matches(/^\d{4}$/, "Enter a valid 4-digit OTP")
  // .required("OTP is required"),
  password: passwordValidator,
  confirmPwd: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords not match")
    .required("Confirm password is required"),
});

export const submitPrSchema = yup.object().shape({
  packageId: yup.string().required("Please select package before proceeding."),
  // packageId: yup.string().notRequired(),
  categoryId: yup.string().required("Please select at least one category."),
  title: yup
    .string()
    .required("Headline is required")
    .min(50, "Headline must be at least 50 characters")
    .max(200, "Headline must not exceed 200 characters"),
  editorData: yup
    .string()
    .test(
      "editorData",
      "Press Release Body is required",
      (value) => {
        if (!value) return false;
        const text = value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
        return text.length > 0;
      }
    ),
  // city: yup.string().required("Please select a city/state."),
  // addNote: yup.string().required("Note for editor is required"),
  company: yup.string().nullable(),
  name: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("Company name is required and must be under 100 characters").max(100, "Company name must be at most 100 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  contactPerson: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) =>
      schema
        .required("Contact Person is required")
        .max(100, "Contact person name must be at most 100 characters")
        .matches(/^[A-Za-z\s]*$/, "Contact person name should only contain letters and spaces"),
  }),
  email: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) =>
      schema
        .required("Email is required")
        .email("Please enter a valid email address"),
  }),
  isdCode: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("ISD code is required"),
  }),
  mobile: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("Phone number is required").matches(/^[0-9]{7,15}$/, "Enter a valid phone number (only numbers, max 15 digits)"),
  }),
  website: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) =>
      schema
        .required("Website is required")
        .url("Please enter a valid website URL (e.g. https://example.com)"),
    otherwise: (schema) => schema.notRequired(),
  }),
  address: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("Address is required"),
  }),
  city: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("City is required and must be under 50 characters.").max(50, "City must be at most 50 characters"),
  }),
  state: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("State is required and must be under 50 characters.").max(50, "State must be at most 50 characters"),
  }),
  country: yup.string().when("company", {
    is: (val) => !val || val === "ADD_NEW",
    then: (schema) => schema.required("Please select a country."),
  }),
  image: yup.string().notRequired(),
});
export const SubAccountSchema = yup.object().shape({
  name: yup
    .string()
    .required("Company name is required")
    .max(100, "Company name must be at most 100 characters"),
  contactPerson: yup
    .string()
    .required("Contact person is required")
    .max(100, "Contact person must be at most 100 characters"),
  email: noDisposableEmailValidator,
  isdCode: yup.string().required("isdCode is required"),
  mobile: yup.string()
    .matches(/^[0-9]{7,15}$/, "Invalid phone number")
    .required("Phone number is required"),
  website: websiteValidator("Invalid URL"),
  // createdBy: yup.string().required("Created by is required"),
  address: yup.string().required("Address is required").max(200, "Company address must be at most 200 characters"),
  city: yup.string().required("City is required").max(50, "City must be at most 50 characters"),
  state: yup.string().required("State is required").max(50, "Country must be at most 50 characters"),
  country: yup.string().required("Country is required"),
});

export const editPrSchema = yup.object().shape({
  packageId: yup.string().required("Please select package before proceeding."),
  categoryId: yup.string().required("Please select at least one category."),
  companyId: yup.string().required("Please select company before proceeding."),
  title: yup
    .string()
    .required("Headline is required")
    .min(50, "Headline must be at least 50 characters")
    .max(200, "Headline must not exceed 200 characters"),
  // editorData: yup
  // .string()
  // .test(
  //   "editorData",
  //   "PR Body is required",
  //   (value) => {
  //     if (!value) return false;
  //     const text = value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  //     return text.length > 0;
  //   }
  // ),
});

export const supportSchema = yup.object().shape({
  subject: yup.string().required("Subject is required."),
  hlpReason: yup.string().required("Please select the reason."),
  description: yup.string().required("Description is required.")
});
